import { gql } from "@apollo/client";
import { AdminLayout } from "components/admin/admin-layout";
import { EventError } from "components/admin/events/errors";
import { BookClubForm } from "components/admin/events/new/bookclub";
import { FestivalEventForm } from "components/admin/events/new/festival";
import { OnlineEventForm } from "components/admin/events/new/online";
import { StandardEventForm } from "components/admin/events/new/standard";
import { Button } from "components/buttons/button";
import { BookSelectorValue } from "components/forms/book-selector";
import { CategorySelectorValue } from "components/forms/category-selector";
import { FestivalSelectorValue } from "components/forms/festival-selector";
import { saveDraftContent } from "components/text-editor/editor-utils";
import currency from "currency.js";
import { EditorState } from "draft-js";
import { DateUtil, HasuraFormat } from "lib/date";
import {
  InsertNewEventMutationVariables,
  useInsertNewEventMutation,
} from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { useRouter } from "next/router";
import { useState } from "react";
import slugify from "slugify";
import { EventType } from "../../../components/events/types";
import { InPersonEventForm } from "components/admin/events/new/inperson";

const ADD_EVENT = gql`
  mutation InsertNewEvent($data: events_insert_input!) {
    insert_events_one(object: $data) {
      slug
    }
  }
`;

export const NewEvent = () => {
  const router = useRouter();

  const title = useInput<string>("");
  const description = useInput<EditorState>(null);
  const startDate = useInput<Date>(new Date());
  const endDate = useInput<Date>(new Date());
  const speakers = useInput<string>("");
  const image = useInput<string>("");
  const books = useInput<BookSelectorValue[]>([]);
  const categories = useInput<CategorySelectorValue[]>([]);
  const price = useInput<number>(0);
  const seats = useInput<number>(0);
  const freeSeats = useInput<number>(0);
  const partOfFestival = useInput<FestivalSelectorValue[]>([]);
  const location = useInput<string>("");
  const videoUrl = useInput<string>("");
  const [type, setType] = useState<EventType>("standard");
  const videoConfLocation = useInput<string>("");

  const [save, { error, data, loading }] = useInsertNewEventMutation({
    errorPolicy: "none",
  });

  const [eventCanSave, setCanSave] = useState<boolean>(false);

  let canSaveBasic =
    title.isModified &&
    title.value.trim() !== "" &&
    image.isModified &&
    startDate.isModified &&
    endDate.isModified &&
    description.isModified;

  let canSave: boolean = canSaveBasic && eventCanSave;

  const handleSave = (e) => {
    e.preventDefault();

    let variables: InsertNewEventMutationVariables = {
      data: {
        title: title.value,
        slug: slugify(title.value, { lower: true, strict: true }),
        description: saveDraftContent(description.value),
        start_time: DateUtil(startDate.value).format(HasuraFormat),
        end_time: DateUtil(endDate.value).format(HasuraFormat),
        image: image.value,
        price: currency(price.value).intValue,
      },
    };

    switch (type) {
      case "standard":
        variables.data.event_books = {
          data: books.value.map((b) => ({ book_isbn: b.data.isbn })),
        };
        variables.data.category_events = { data: null };
        variables.data.category_events.data = categories.value.map((c) => ({
          category_name: c.data.name,
        }));
        variables.data.seats = seats.value;
        variables.data.free_seats = freeSeats.value;
        variables.data.location =
          location.value.trim() !== "" ? location.value : null;
        (variables.data.speakers = speakers.value),
          (variables.data.festival_id = partOfFestival.value[0]?.data.id);
        variables.data.video = videoUrl.value;
        variables.data.video_conf_location = videoConfLocation.value;
        break;
      case "online-only":
        variables.data.is_online_only = true;
        variables.data.event_books = {
          data: books.value.map((b) => ({ book_isbn: b.data.isbn })),
        };
        variables.data.category_events = { data: null };
        variables.data.category_events.data = categories.value.map((c) => ({
          category_name: c.data.name,
        }));
        variables.data.speakers = speakers.value;
        variables.data.festival_id = partOfFestival.value[0]?.data.id;
        variables.data.video = videoUrl.value;
        variables.data.video_conf_location = videoConfLocation.value;
        break;
      case "in-person-only":
        variables.data.is_in_person_only = true;
        variables.data.event_books = {
          data: books.value.map((b) => ({ book_isbn: b.data.isbn })),
        };
        variables.data.category_events = { data: null };
        variables.data.category_events.data = categories.value.map((c) => ({
          category_name: c.data.name,
        }));
        variables.data.seats = seats.value;
        variables.data.free_seats = freeSeats.value;
        variables.data.location =
          location.value.trim() !== "" ? location.value : null;
        (variables.data.speakers = speakers.value),
          (variables.data.festival_id = partOfFestival.value[0]?.data.id);
        break;
      case "book-club":
        variables.data.is_book_club = true;
        variables.data.category_events = { data: null };
        variables.data.category_events.data = categories.value.map((c) => ({
          category_name: c.data.name,
        }));
        variables.data.event_books = {
          data: books.value.map((b) => ({ book_isbn: b.data.isbn })),
        };
        variables.data.category_events = { data: null };
        variables.data.category_events.data = categories.value.map((c) => ({
          category_name: c.data.name,
        }));
        variables.data.seats = seats.value;
        variables.data.free_seats = seats.value;
        variables.data.is_book_club = true;
        break;
      case "festival":
        variables.data.is_festival = true;
        variables.data.category_events = { data: null };
        variables.data.category_events.data = categories.value.map((c) => ({
          category_name: c.data.name,
        }));
        variables.data.location =
          location.value.trim() !== "" ? location.value : null;
        break;
    }

    save({
      variables: variables,
    })
      .then(({ data }) => {
        if (data) {
          router.push(`/admin/events/${data.insert_events_one.slug}`);
        }
      })
      .catch((e) => null);
  };

  return (
    <>
      <AdminLayout page='New Event'>
        <article className='space-y-4'>
          <header className='flex flex-row justify-between'>
            <h1 className='header text-4xl'>Creating New Event</h1>
            <section className='space-x-4'>
              <Button
                buttonType='primary'
                buttonSize='md'
                disabled={!canSave}
                onClick={handleSave}>
                Save
              </Button>
              <Button buttonType='ghost' buttonSize='md' href='/admin/events'>
                Cancel
              </Button>
            </section>
          </header>
          <EventError error={error} />
          <form
            className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'
            onSubmit={handleSave}>
            <fieldset className='flex flex-row col-span-full w-full justify-start space-x-16'>
              <label
                htmlFor='standard'
                className=' flex flex-row items-center '>
                Standard
                <input
                  radioGroup='type'
                  className='w-10 h-10 ml-5'
                  type='radio'
                  name='standard'
                  id='standard'
                  checked={type === "standard"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setType("standard");
                    }
                  }}
                />
              </label>

              <label
                htmlFor='isFestival'
                className=' flex flex-row items-center '>
                Festival?
                <input
                  radioGroup='type'
                  className='w-10 h-10 ml-5'
                  type='radio'
                  name='isFestival'
                  id='isFestival'
                  checked={type === "festival"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setType("festival");
                    }
                  }}
                />
              </label>

              <label
                htmlFor='onlineOnly'
                className=' flex flex-row items-center '>
                Online Only?
                <input
                  radioGroup='type'
                  className='w-10 h-10 ml-5'
                  type='radio'
                  name='onlineOnly'
                  id='onlineOnly'
                  checked={type === "online-only"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setType("online-only");
                    }
                  }}
                />
              </label>
              <label
                htmlFor='inPersonOnly'
                className=' flex flex-row items-center '>
                In Person Only?
                <input
                  radioGroup='type'
                  className='w-10 h-10 ml-5'
                  type='radio'
                  name='inPersonOnly'
                  id='inPersonOnly'
                  checked={type === "in-person-only"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setType("in-person-only");
                    }
                  }}
                />
              </label>
              <label
                htmlFor='bookClub'
                className=' flex flex-row items-center '>
                Book Club?
                <input
                  radioGroup='type'
                  className='w-10 h-10 ml-5'
                  type='radio'
                  name='bookClub'
                  id='bookClub'
                  checked={type === "book-club"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setType("book-club");
                    }
                  }}
                />
              </label>
            </fieldset>
            {type === "book-club" ? (
              <BookClubForm
                books={books}
                categories={categories}
                description={description}
                endDate={endDate}
                image={image}
                seats={seats}
                startDate={startDate}
                title={title}
                setCanSave={setCanSave}
              />
            ) : type === "online-only" ? (
              <OnlineEventForm
                books={books}
                categories={categories}
                description={description}
                endDate={endDate}
                image={image}
                startDate={startDate}
                title={title}
                partOfFestival={partOfFestival}
                price={price}
                speakers={speakers}
                videoUrl={videoUrl}
                setCanSave={setCanSave}
                videoConfLocation={videoConfLocation}
              />
            ) : type === "festival" ? (
              <FestivalEventForm
                location={location}
                categories={categories}
                description={description}
                endDate={endDate}
                image={image}
                startDate={startDate}
                title={title}
                setCanSave={setCanSave}
              />
            ) : type === "in-person-only" ? (
              <InPersonEventForm
                freeSeats={freeSeats}
                location={location}
                seats={seats}
                books={books}
                categories={categories}
                description={description}
                endDate={endDate}
                image={image}
                startDate={startDate}
                title={title}
                partOfFestival={partOfFestival}
                price={price}
                speakers={speakers}
                setCanSave={setCanSave}
              />
            ) : (
              <StandardEventForm
                books={books}
                categories={categories}
                description={description}
                endDate={endDate}
                image={image}
                startDate={startDate}
                title={title}
                partOfFestival={partOfFestival}
                price={price}
                speakers={speakers}
                videoUrl={videoUrl}
                freeSeats={freeSeats}
                location={location}
                seats={seats}
                setCanSave={setCanSave}
                videoConfLocation={videoConfLocation}
              />
            )}
          </form>
        </article>
      </AdminLayout>
    </>
  );
};

export default NewEvent;
